import { OrderHistoryModel } from '@store/customers/customerDetails/orderHistory';
import { PendingApprovals } from '@store/Global/types';
import { HoldsInfo } from '@store/holdsTab';
import { OldLoad } from '@store/loadboard';
import { LoadSummaryHeaderViewModel } from '@store/loadBoardSummaryHeader';
import { LoadSearch } from '@store/orders';
import {
  LoadsCoveredType,
  RevenueByGroupType,
  ProductivityGridDataType,
  RevenueBySalesManagerType,
  RevenueStatsType,
  RevenueByUserType,
  CreditHoldsType,
  SettlementsType,
  DashboardInfoType,
} from '@store/revenueDashboard';
import { ReleaseNoteType } from '@typeDefinitions/releaseNotesTypes';
import Dexie, { Table, type EntityTable } from 'dexie';
import { createSignal } from 'solid-js';
// import { applyEncryptionMiddleware } from 'dexie-encrypted';

type OrderHistoryT = {
  hash: number;
  items: OrderHistoryModel[];
  date: Date;
  totalCount: number;
  customerId: number;
};

type PendingApprovalT = {
  items: PendingApprovals[];
  cacheDate: number;
  cacheDuration: number;
};

type RevenueStatsT = {
  items: RevenueStatsType[];
  cacheDate: number;
  cacheDuration: number;
};
type ProductivityT = {
  items: ProductivityGridDataType[];
  cacheDate: number;
  cacheDuration: number;
};

type RevenueBySalesManagerT = {
  items: RevenueBySalesManagerType[];
  cacheDate: number;
  cacheDuration: number;
};

type LoadCoveredByUserT = {
  items: LoadsCoveredType[];
  cacheDate: number;
  cacheDuration: number;
};

type RevenueByGroupT = {
  items: RevenueByGroupType[];
  cacheDate: number;
  cacheDuration: number;
};

type RevenueByUserT = {
  items: RevenueByUserType[];
  cacheDate: number;
  cacheDuration: number;
};

type CreditHoldsTypeT = {
  items: CreditHoldsType[];
  cacheDate: number;
  cacheDuration: number;
};

type SettlementsTypeT = {
  items: SettlementsType;
  cacheDate: number;
  cacheDuration: number;
};

type HoldsInfoT = {
  items: HoldsInfo[];
  cacheDate: number;
  cacheDuration: number;
};

type OldLoadT = {
  items: {
    data: OldLoad[];
    totalCount: number;
  };
  cacheDate: number;
  cacheDuration: number;
};

type DashboardInfoTypeT = {
  items: DashboardInfoType;
  cacheDate: number;
  cacheDuration: number;
};

type UserInfoT = {
  userId: number;
};

type ReleaseNoteT = {
  items: ReleaseNoteType;
  cacheDate: number;
  cacheDuration: number;
};

type LoadSummaryHeaderT = {
  items: LoadSummaryHeaderViewModel;
  cacheDate: number;
  cacheDuration: number;
};

export type StateDBT = Dexie & {
  loadSearchItems: EntityTable<LoadSearch, 'loadId'>;
  orderHistory: EntityTable<OrderHistoryT, 'hash'>;
  pendingApprovals: Table<PendingApprovalT & { key: string }, string>;
  revenueStats: Table<RevenueStatsT & { key: string }, string>;
  revenueBySalesManager: Table<
    RevenueBySalesManagerT & { key: string },
    string
  >;
  loadsCoveredByUser: Table<LoadCoveredByUserT & { key: string }, string>;
  revenueByGroup: Table<RevenueByGroupT & { key: string }, string>;
  productivity: Table<ProductivityT & { key: string }, string>;
  revenueByUser: Table<RevenueByUserT & { key: string }, string>;
  creditHolds: Table<CreditHoldsTypeT & { key: string }, string>;
  pendingCommission: Table<SettlementsTypeT & { key: string }, string>;
  billingHolds: Table<HoldsInfoT & { key: string }, string>;
  oldLoads: Table<OldLoadT & { key: string }, string>;
  dashboardInfo: Table<DashboardInfoTypeT & { key: string }, string>;
  userInfo: Table<UserInfoT & { key: string }, string>;
  // orderHistoryItems: EntityTable<OrderHistoryModel, 'orderId'>;
  releaseNotesLatest: Table<ReleaseNoteT & { key: string }, string>;
  loadSummaryHeader: Table<LoadSummaryHeaderT & { key: string }, string>;
};

export const [getDB, setDB] = createSignal<StateDBT>();

export const initDatabase = async () => {
  const db = new Dexie('state-db') as StateDBT;

  db.version(1).stores({
    loadSearchItems:
      'loadId, orderId, loadNumber, customerId, customer, status, carrier, mode, equipment, agent, loadType, coveredBy, accountManager, salesManager, office, officeId, originCity, originState, destinationCity, destinationState, pickup, pickupTime, dropoff, dropoffTime, trackingNumber, trailerNumber, referenceNum, carrierPay, customerPay, loadCreatedDate, loadBilledDate, claim, orderStatus, carrierContactName, carrierContactType, carrierContactPhone, carrierContactEmail, weight, pieces, product',
    orderHistory: 'hash, items, date, totalCount, customerId',
    pendingApprovals: 'key',
    revenueStats: 'key',
    revenueBySalesManager: 'key',
    loadsCoveredByUser: 'key',
    revenueByGroup: 'key',
    productivity: 'key',
    revenueByUser: 'key',
    creditHolds: 'key',
    pendingCommission: 'key',
    billingHolds: 'key',
    oldLoads: 'key',
    dashboardInfo: 'key',
    userInfo: 'key',
    // orderHistoryItems:
    //   'agent, cost, customerId, destinationCity, destinationState, dropoff, numLoads, orderId, originCity, originState, pickoff, proNumber, profit, referenceNumber, revenue, status, stopDropOffNumber, stopPickupNumber, trackingNumber, loadProduct',
    releaseNotesLatest: 'key',
    loadSummaryHeader: 'key',
  });

  setDB(db);
  await db.open();

  return db;
};

// @ts-expect-error dsadsa
window.getDB = getDB;
